/**
 * The EventFeed — the ONLY input the renderer has.
 *
 * `render/` never talks to `net/`. Live play pushes frames from a socket;
 * replay pushes frames parsed from `log.jsonl`. The renderer consumes this
 * object and cannot tell the two apart, which is what makes "the replay is the
 * same code path as the live session" structural rather than
 * aspirational.
 *
 * The state reduction itself is `applyToSnapshot` from `@overcooked/shared` —
 * the very function the server uses to replay a stored log. This class owns the
 * things a *renderer* needs and the server does not: the transient cues, the
 * latest voice round trips, and an estimate of where the server clock is right
 * now so a cook bar can move between frames.
 */

import type { Cell, EventFrame, GameEvent, PlayerId, SerialisedState } from '@overcooked/shared'
import { applyToSnapshot, TICK_MS } from '@overcooked/shared'
import { CONTESTED_REASON } from '../render/reasons'
import { TickClock } from '../render/tickClock'

/** How long a 💥 or a blocked-bump stays on screen. */
export const CUE_LIFETIME_MS = 450

/**
 * Longer than a 💥, because a ticket leaving the queue is a *number* — a score
 * that went up or down — and a number has to be read rather than glimpsed.
 */
export const ORDER_CUE_LIFETIME_MS = 2200

/**
 * How long a refusal sentence stays in the flash line.
 *
 * Longer than the cue on the cell, because a shape is glimpsed and a sentence
 * is read — 450ms is not enough time to read "no clean plates — wash one at the
 * sink" while also watching a chef.
 */
export const REFUSAL_LIFETIME_MS = 2000

/**
 * How long past its own last tick a round is allowed to go on before the client
 * stops waiting for the whistle and blows it itself.
 *
 * The bug this closes was reported as "the clock reached zero and the round
 * kept going". The server is not what kept going — the session with `lastTick:
 * 9000` stopped on exactly 180 s — so the round that carried on was the one on
 * the screen, which had nothing to end it but a `ROUND_ENDED` off the socket.
 * A frame that never arrives is therefore a round that never ends, and the
 * player is left looking at 0:00 in a kitchen that still takes their keys.
 *
 * So the end is latched on two independent things: the event, and the clock the
 * player is reading. This is the slack the second one gives the first — long
 * enough that the event wins on any ordinary connection (`ROUND_ENDED` is
 * emitted within a tick of the last tick and lands one one-way latency later),
 * short enough that "the clock hits 0:00" and "the end screen opens" are the
 * same second, which is how the fix is checked by eye.
 */
export const ROUND_END_GRACE_MS = 500

/** A ticket arriving, being filled, or running out. Drawn on the HUD, not the canvas. */
export interface OrderCue {
  kind: 'spawned' | 'served' | 'expired'
  orderId: string
  /** `null` for a serve that matched nothing — a real outcome worth showing. */
  dishId: string | null
  scoreDelta: number
  shownAt: number
}

/** How the round went, for the end screen. Cumulative within one round. */
export interface RoundTally {
  /** Plates put through the hatch, matched or not. */
  served: number
  /** Of those, the ones that filled a ticket. */
  matched: number
  expired: number
}

export interface Cue {
  kind: 'collision' | 'blocked' | 'contested' | 'failed'
  cell: Cell
  playerIds: PlayerId[]
  /** Set on `failed` cues: the reason string the server gave. */
  reason?: string
  /** `performance.now()` at push time, not a game tick — cues are decoration. */
  shownAt: number
}

/**
 * The newest refusal aimed at *this* player, for the flash line.
 *
 * A cue on the cell says "not there"; this says why, in words, and only for the
 * player who pressed the key — being told the reason your partner's press
 * failed, on the other side of the kitchen, is noise.
 */
export interface RefusalNote {
  /** An `ACTION_FAILED` reason, or `'contested'`. Looked up in `render/reasons.ts`. */
  reason: string
  /** `performance.now()` at push time, like `Cue.shownAt`. */
  shownAt: number
}

export interface FeedSnapshot {
  state: SerialisedState | null
  cues: Cue[]
  /** `null` when nothing has been refused recently, or when there is no local player. */
  refusal: RefusalNote | null
  /** Recent arrivals and departures from the ticket queue, for the HUD. */
  orderCues: OrderCue[]
  tally: RoundTally
  /**
   * Where the server's clock has most likely got to. Progress bars are drawn
   * from this rather than from `state.tick`, which only moves when a frame
   * arrives — and a pan quietly cooking produces no frames at all.
   */
  tick: number
  /**
   * Set once the round is over — by `ROUND_ENDED`, or by the clock running past
   * the round's own length on a live feed. See `ROUND_END_GRACE_MS`: the
   * whistle is not allowed to be the only thing that can end a round, because
   * one dropped frame then ends it never.
   */
  ended: { reason: 'timer' | 'aborted'; score: number } | null
  /**
   * Latest voice-path round trip per player, from `NETWORK_SAMPLE`. Flows
   * through the same feed as everything else, which means a replay shows the
   * network conditions the players were actually under.
   */
  rttByPlayer: Record<PlayerId, number>
  /**
   * How many `SYNC_BEEP`s have come through. A count rather than a flag so the
   * page can tell "the anchor for this round" from "the anchor for the one
   * before it" with a comparison rather than a reset.
   *
   * This is the only event the feed forwards for its *sound* rather than its
   * picture, and it is why: the tone has to be mixed into the recording at the
   * moment the log says tick 0, and the feed is the only thing that knows when
   * that moment arrived.
   */
  syncBeeps: number
}

type Listener = (snapshot: FeedSnapshot) => void

export interface FeedOptions {
  /** See the constructor: live feeds anchor their clock, replays do not. */
  live?: boolean
  /** The epoch clock the round-start anchor is converted through. For tests. */
  epochClock?: () => number
}

export class EventFeed {
  private state: SerialisedState | null = null
  private cues: Cue[] = []
  private refusal: RefusalNote | null = null
  private selfId: PlayerId | null = null
  private orderCues: OrderCue[] = []
  private tally: RoundTally = emptyTally()
  private ended: FeedSnapshot['ended'] = null
  /** The round's own length, from `ROUND_STARTED`, for feeds with no keyframe yet. */
  private durationTicks = 0
  private rttByPlayer: Record<PlayerId, number> = {}
  private syncBeeps = 0
  private listeners = new Set<Listener>()
  private clock: () => number
  private tickClock: TickClock
  private live: boolean

  /**
   * @param clock the local monotonic clock; injected so cue expiry is a fact
   *   rather than a sleep.
   * @param options.live whether a `ROUND_STARTED` in this stream is *happening*
   *   rather than being *watched*. A live feed anchors its tick clock to the
   *   round's start; the replay page must not, because a log played back an
   *   hour later would anchor to an hour ago and run its clock to the end of
   *   the round on the first frame.
   */
  constructor(clock: () => number = () => performance.now(), options: FeedOptions = {}) {
    this.clock = clock
    this.live = options.live ?? true
    this.tickClock = new TickClock({ epochNow: options.epochClock })
  }

  /**
   * Whose refusals are worth words.
   *
   * Set once the seat is known, and left `null` on `/replay` — a replay has no
   * "you", so it shows the cues on the cells and no sentence.
   */
  setSelfId(id: PlayerId | null): void {
    if (this.selfId === id) return
    this.selfId = id
    this.refusal = null
  }

  /**
   * A refusal that never reached the engine.
   *
   * The transport drops input for four reasons — rate limited, no seat, no
   * round, malformed — and used to do it in perfect silence. Those refusals
   * have no cell and no tick, so they cannot be cues; they are words only, and
   * they come in here rather than through `push` because they are not events
   * and must never be mistaken for the tape.
   */
  refuse(reason: string): void {
    this.refusal = { reason, shownAt: this.clock() }
    this.notify()
  }

  reset(): void {
    this.state = null
    this.cues = []
    this.refusal = null
    this.orderCues = []
    this.tally = emptyTally()
    this.ended = null
    this.durationTicks = 0
    this.rttByPlayer = {}
    this.syncBeeps = 0
    this.tickClock.reset()
    this.notify()
  }

  push(frame: EventFrame): void {
    // Events first: `ROUND_STARTED` anchors the clock, and it has to be
    // anchored before the frame that carries it corrects against it.
    for (const event of frame.events) this.apply(event)
    // The *folded* tick, not `frame.tick`: a frame carrying tick N is the
    // events of tick N, and the state that comes out of folding it is the state
    // at N+1. That is the number every bar in the kitchen is measured against,
    // so it is the one the clock has to agree with.
    this.tickClock.onFrame(this.state?.tick ?? frame.tick, this.clock())
    this.notify()
  }

  /** The renderer's read. Expired cues are dropped here, not on a timer. */
  snapshot(): FeedSnapshot {
    const now = this.clock()
    this.cues = this.cues.filter((c) => now - c.shownAt < CUE_LIFETIME_MS)
    this.orderCues = this.orderCues.filter((c) => now - c.shownAt < ORDER_CUE_LIFETIME_MS)
    if (this.refusal && now - this.refusal.shownAt >= REFUSAL_LIFETIME_MS) this.refusal = null
    const tick = this.state ? this.tickClock.estimate(now) : 0
    this.latchEndOnClock(tick)
    return {
      state: this.state,
      cues: this.cues,
      refusal: this.refusal,
      orderCues: this.orderCues,
      tally: this.tally,
      tick,
      ended: this.ended,
      rttByPlayer: this.rttByPlayer,
      syncBeeps: this.syncBeeps,
    }
  }

  /** Live state for the React chrome, which needs re-render on discrete change. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify(): void {
    const snapshot = this.snapshot()
    for (const listener of this.listeners) listener(snapshot)
  }

  /**
   * The second way a round ends: its own clock ran out.
   *
   * Read on every `snapshot()` rather than pushed from anywhere, because the
   * whole point is that no frame is coming — a round whose last frame was
   * dropped produces no events at all, so anything waiting to be *told* waits
   * forever. It is a latch and not a computed field so the end screen's score
   * is fixed at the moment the round ended rather than moving afterwards.
   *
   * Three guards, and each one is a claim about who is allowed to say this:
   *
   *   - `live`: a replay's round ends where its tape says it ended. The replay
   *     page's clock is unanchored and extrapolates, so letting it end a round
   *     would let an extrapolation close a recording early.
   *   - `anchored`: only a clock tied to this round's own `startedAtEpochMs`
   *     knows what time it is. The unanchored fallback is a guess by
   *     construction and must not be allowed to blow a whistle.
   *   - the grace: the event is still the normal way a round ends, and it is
   *     given room to arrive first so `reason` is the server's word, not ours.
   */
  private latchEndOnClock(tick: number): void {
    if (this.ended !== null || !this.live || !this.state) return
    if (!this.tickClock.anchored) return
    const duration = this.state.roundDurationTicks || this.durationTicks
    if (duration <= 0) return
    if (tick < duration + ROUND_END_GRACE_MS / TICK_MS) return
    // 'timer' rather than 'aborted': the clock is what ended it, which is the
    // same thing the server would have said had its frame arrived.
    this.ended = { reason: 'timer', score: this.state.score }
  }

  private cue(cue: Omit<Cue, 'shownAt'>): void {
    this.cues = [...this.cues, { ...cue, shownAt: this.clock() }]
  }

  private orderCue(cue: Omit<OrderCue, 'shownAt'>): void {
    this.orderCues = [...this.orderCues, { ...cue, shownAt: this.clock() }]
  }

  /** Newest wins: two refusals inside two seconds means the second one is the live question. */
  private noteRefusal(playerIds: PlayerId[], reason: string): void {
    if (!this.selfId || !playerIds.includes(this.selfId)) return
    this.refusal = { reason, shownAt: this.clock() }
  }

  private apply(event: GameEvent): void {
    // Cues first: they are what this class adds on top of the shared fold.
    switch (event.type) {
      case 'ROUND_STARTED':
        this.ended = null
        this.durationTicks = event.durationTicks
        this.tally = emptyTally()
        this.orderCues = []
        this.refusal = null
        this.cues = []
        // A new round in the same room restarts the server's tick at zero, but
        // the fold only ever moves `tick` forwards (`Math.max`), so last round's
        // kitchen would carry its final tick — ~9000 on the shipped clock — into
        // this one. The tick clock floors on that number, the HUD reads the
        // round as over from its first frame, and the end latch then blows the
        // whistle a few seconds in while the server plays the full round. So a
        // kitchen from a later tick than this opener is last round's, and it is
        // dropped; the opener's own KEYFRAME, one event behind, brings the new
        // one. A replay whose tape put its keyframe first (tick 1, not later
        // than the opener's) keeps it.
        if (this.state && this.state.tick > event.tick + 1) this.state = null
        // The one moment the client learns where the server's clock started.
        // Every bar, fuse and the round clock itself are drawn against it from
        // here on, instead of against the last frame that happened to arrive.
        this.tickClock.reset()
        if (this.live) this.tickClock.anchor(event.startedAtEpochMs, this.clock())
        break

      case 'ROUND_ENDED':
        this.ended = { reason: event.reason, score: event.score }
        break

      case 'SYNC_BEEP':
        this.syncBeeps += 1
        break

      // -- the ticket queue ---------------------------------------------
      //
      // These three are the only events whose consequence is a number rather
      // than a picture, so the HUD gets a cue per event and a running tally
      // per round. Without them a ticket lapsing is a row silently vanishing
      // from a list, which is exactly the moment the players most need told.

      case 'ORDER_SPAWNED':
        this.orderCue({ kind: 'spawned', orderId: event.order.id, dishId: event.order.dishId, scoreDelta: 0 })
        break

      case 'ORDER_EXPIRED':
        this.tally = { ...this.tally, expired: this.tally.expired + 1 }
        this.orderCue({
          kind: 'expired',
          orderId: event.orderId,
          dishId: this.state?.orders.find((o) => o.id === event.orderId)?.dishId ?? null,
          scoreDelta: event.scoreDelta,
        })
        break

      case 'SERVED':
        this.tally = {
          ...this.tally,
          served: this.tally.served + 1,
          matched: this.tally.matched + (event.matchedOrderId ? 1 : 0),
        }
        this.orderCue({
          kind: 'served',
          orderId: event.matchedOrderId ?? event.plateId,
          dishId: event.matchedOrderId
            ? (this.state?.orders.find((o) => o.id === event.matchedOrderId)?.dishId ?? null)
            : null,
          scoreDelta: event.scoreDelta,
        })
        break

      case 'COLLISION':
        this.cue({ kind: 'collision', cell: event.cell, playerIds: event.playerIds })
        break

      case 'CONTESTED':
        // Both players get this, not just the one who lost. That visibility is
        // the whole fix for the Craft Together contested-pickup failure mode.
        this.cue({ kind: 'contested', cell: event.cell, playerIds: event.playerIds })
        this.noteRefusal(event.playerIds, CONTESTED_REASON)
        break

      case 'PLAYER_BLOCKED':
        this.cue({ kind: 'blocked', cell: event.cell, playerIds: [event.playerId] })
        break

      case 'ACTION_FAILED': {
        // A refusal is drawn on the cell the player was aiming at, so "that did
        // not work" and "at that station" are one glance rather than two.
        const player = this.state?.players.find((p) => p.id === event.playerId)
        if (player) {
          this.cue({
            kind: 'failed',
            cell: aheadOf(player.cell, player.facing),
            playerIds: [event.playerId],
            reason: event.reason,
          })
        }
        // Outside the guard on purpose: if the actor is not in local state yet
        // the cue has nowhere to go, and the sentence is then the only feedback
        // the player gets. That silence was the third of the three.
        this.noteRefusal([event.playerId], event.reason)
        break
      }

      case 'NETWORK_SAMPLE':
        this.rttByPlayer = { ...this.rttByPlayer, [event.playerId]: event.rttMs }
        break

      default:
        break
    }

    // Then the state, folded by exactly the same function the server replays
    // a stored log with.
    if (event.type === 'KEYFRAME' || this.state) {
      this.state = applyToSnapshot(this.state ?? EMPTY, event)
    }
  }
}

/**
 * The pre-keyframe placeholder. A client that connects mid-round renders
 * nothing until the first KEYFRAME arrives, which is one round trip away.
 */
const EMPTY: SerialisedState = {
  tick: 0,
  mapName: '',
  players: [],
  stations: [],
  orders: [],
  pendingPlates: [],
  score: 0,
  roundDurationTicks: 0,
  orderPool: [],
}

function emptyTally(): RoundTally {
  return { served: 0, matched: 0, expired: 0 }
}

function aheadOf(cell: Cell, facing: string): Cell {
  if (facing === 'up') return { x: cell.x, y: cell.y - 1 }
  if (facing === 'down') return { x: cell.x, y: cell.y + 1 }
  if (facing === 'left') return { x: cell.x - 1, y: cell.y }
  return { x: cell.x + 1, y: cell.y }
}
